/**
 * The tools of the rich-text editor's toolbar. Toolbars are written as groups of item names (Bold,
 * NumberedList, FontSize, ...), the names the panel's editor configs used before the move from
 * CKEditor 4, so a config reads the same and a group becomes a run of tools between separators.
 */
export type RichTextTool =
  | 'undo' | 'redo'
  | 'bold' | 'italic' | 'underline' | 'strike' | 'removeFormat'
  | 'numberedList' | 'bulletedList' | 'outdent' | 'indent' | 'alignLeft' | 'alignCenter' | 'alignRight'
  | 'link' | 'unlink' | 'image'
  | 'format' | 'font' | 'fontBrowser' | 'fontSize' | 'textColor' | 'bgColor'
  | 'maximize' | 'variable' | 'plainText'
  | '|';

const TOOLBAR_ITEMS: Record<string, RichTextTool> = {
  Undo: 'undo', Redo: 'redo',
  Bold: 'bold', Italic: 'italic', Underline: 'underline', Strike: 'strike', RemoveFormat: 'removeFormat',
  NumberedList: 'numberedList', BulletedList: 'bulletedList', Outdent: 'outdent', Indent: 'indent',
  JustifyLeft: 'alignLeft', JustifyCenter: 'alignCenter', JustifyRight: 'alignRight',
  Link: 'link', Unlink: 'unlink', Image: 'image',
  Format: 'format', Font: 'font', SbFont: 'fontBrowser', FontSize: 'fontSize', TextColor: 'textColor', BGColor: 'bgColor',
  Maximize: 'maximize', SbVariable: 'variable',
};

/** Groups of item names ('-' a separator inside a group) as tools; a group ends with a separator. */
export const toolsFromToolbarGroups = (groups: Array<{items: string[]}>): RichTextTool[] => {
  const tools: RichTextTool[] = [];
  for (const group of groups || []) {
    const items = (group.items || []).map((item) => item === '-' ? '|' : TOOLBAR_ITEMS[item]).filter(Boolean);
    if (items.length) {
      if (tools.length && tools[tools.length - 1] !== '|') {
        tools.push('|');
      }
      tools.push(...items);
    }
  }
  return tools;
};

/** The menus' choices, as the editor offered them before (CKEditor 4's defaults). */
export const FONT_SIZES = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px', '28px', '36px', '48px', '72px'];

export const FONT_NAMES: Array<{label: string; family: string}> = [
  {label: 'Arial', family: 'Arial, Helvetica, sans-serif'},
  {label: 'Comic Sans MS', family: 'Comic Sans MS, cursive'},
  {label: 'Courier New', family: 'Courier New, Courier, monospace'},
  {label: 'Georgia', family: 'Georgia, serif'},
  {label: 'Lucida Sans Unicode', family: 'Lucida Sans Unicode, Lucida Grande, sans-serif'},
  {label: 'Tahoma', family: 'Tahoma, Geneva, sans-serif'},
  {label: 'Times New Roman', family: 'Times New Roman, Times, serif'},
  {label: 'Trebuchet MS', family: 'Trebuchet MS, Helvetica, sans-serif'},
  {label: 'Verdana', family: 'Verdana, Geneva, sans-serif'},
];

/** The colour palette the editor offered before (CKEditor 4's), written lower case. */
export const COLORS: Array<{name: string; value: string}> = [
  ['Strong Cyan', '1abc9c'], ['Emerald', '2ecc71'], ['Bright Blue', '3498db'], ['Amethyst', '9b59b6'],
  ['Grayish Blue', '4e5f70'], ['Vivid Yellow', 'f1c40f'], ['Dark Cyan', '16a085'], ['Dark Emerald', '27ae60'],
  ['Strong Blue', '2980b9'], ['Dark Violet', '8e44ad'], ['Desaturated Blue', '2c3e50'], ['Orange', 'f39c12'],
  ['Carrot', 'e67e22'], ['Pale Red', 'e74c3c'], ['Bright Silver', 'ecf0f1'], ['Light Grayish Cyan', '95a5a6'],
  ['Light Gray', 'dddddd'], ['White', 'ffffff'], ['Pumpkin', 'd35400'], ['Strong Red', 'c0392b'],
  ['Silver', 'bdc3c7'], ['Grayish Cyan', '7f8c8d'], ['Dark Gray', '999999'], ['Black', '000000'],
].map(([name, value]) => ({name, value: `#${value}`}));

export const FORMATS: Array<{label: string; level: number}> = [
  {label: 'Normal', level: 0},
  {label: 'Heading 1', level: 1}, {label: 'Heading 2', level: 2}, {label: 'Heading 3', level: 3},
  {label: 'Heading 4', level: 4}, {label: 'Heading 5', level: 5}, {label: 'Heading 6', level: 6},
];
